import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Play,
  RotateCcw,
  CheckCircle2,
  XCircle,
  ClipboardList,
  Info,
  LoaderCircle,
} from 'lucide-react'
import Breadcrumb  from '../../components/layout/Breadcrumb'
import {
  Card, CardHeader, CardBody,
  PageHeader, StatusBadge, EmptyState, Alert, Spinner,
} from '../../components/ui'
import { FormField, Input, Select } from '../../components/ui/FormField'
import { useRunAudit } from '../../hooks/useAudit'
import { computeAuditResult } from '../../api/auditApi'
import { formatCurrency, formatDateTime } from '../../utils'
import { US_STATES } from '../../config/constants'
import { downloadRaterFile } from '../../api/downloadRater';

/* ─── Zod validation schema ─────────────────────────── */
const auditSchema = z.object({
  policy: z
    .string()
    // .min(1, 'Policy number is required')
    // .min(3, 'Policy number must be at least 3 characters')
    // .max(50, 'Policy number is too long')
    .min(3, 'Policy Number must be 3 characters'),
    // .max(18, 'Policy Number must not exceed 16 characters')
    // .min(10,'Policy Number is too short')
    // // .regex(/^[A-Za-z0-9\-_]+$/, 'Only letters, numbers, hyphens and underscores allowed')
    //  .regex(
    //   /^TXRPBG-\d{7,8}-00$/,
    //   'Policy Number must be in format TXRPBG-1234567-00'
    // ),
  // stateCode: z
  //   .string()
  //   .min(1, 'Please select a state'),
})

/* ─── Result Card ────────────────────────────────────── */
function ResultCard({ result }) {
  const {
    computed,
    policy,
    stateCode,
    timestamp,
    raterFileName,
    raterDriveFileId,
  } = result

  const [isDownloading, setIsDownloading] = useState(false)
  console.log("RESULT CARD DATA =", result);
  console.log("RATER FILE NAME =", raterFileName);
  const isMatch = computed.status === 'MATCH'

  const metrics = [
    { label: 'Rater Premium',  value: formatCurrency(computed.raterPremium)       },
    { label: 'UI Premium',     value: formatCurrency(computed.uiPremium)},
    { label: 'Status',         badge: computed.status},
    { label: 'State Code',     value: stateCode,                       mono: true },
    { label: 'Policy Number',  value: policy,                          mono: true },
    { label: 'Difference',     value: formatCurrency(computed.difference), 
      danger: !isMatch },
  ]

  return (
    <Card className={`border-l-4 ${isMatch ? 'border-l-emerald-500' : 'border-l-red-500'} animate-fade-in`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="section-title">Comparison Result</h2>
            <p className="text-xs text-slate-500 mt-0.5 mb-4">
              {formatDateTime(timestamp)}</p>
          </div>
          <StatusBadge status={computed.status} />
        </div>
        {/* Agency Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          <div className="rounded-lg p-4 border bg-blue-50 border-blue-200">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
              Agency Name
            </p>

            <p className="text-lg font-semibold text-slate-900">
              {computed.agencyName || '-'}
            </p>
          </div>

          <div className="rounded-lg p-4 border bg-blue-50 border-blue-200">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
              Agent Name
            </p>

            <p className="text-lg font-semibold text-slate-900">
              {computed.agentName || '-'}
            </p>
          </div>          
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        {/* Status Banner */}
        <div className={`flex items-start gap-3 p-4 rounded-lg ${
          isMatch
            ? 'bg-emerald-50 border border-emerald-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          {isMatch
            ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            : <XCircle      className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          }
          <div>
            <p className={`font-semibold text-sm ${isMatch ? 'text-emerald-800' : 'text-red-800'}`}>
              {isMatch
                ? 'Premiums match — no discrepancy found'
                : 'Premium mismatch detected — review required'}
            </p>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {metrics.map((m, i) => (
            <div
              key={i}
              className={`rounded-lg p-4 border ${
                m.danger
                  ? 'bg-red-50 border-red-200'
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                {m.label}
              </p>
              {m.badge ? (
                <StatusBadge status={m.badge} />
              ) : (
                <p className={`text-lg font-bold ${
                  m.danger     ? 'text-red-700'
                  : m.highlight ? 'text-amber-700'
                  : 'text-slate-900'
                } ${m.mono ? 'font-mono' : ''}`}>
                  {m.value}
                  
                </p>                
              )}              
            </div>    
                          
          ))}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">&nbsp;</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">&nbsp;</div>
            {raterFileName && (
            <a
            href="#"
            onClick={async (e) => {
              e.preventDefault()

              if (isDownloading) return

              try {
                setIsDownloading(true)

                await downloadRaterFile(
                  stateCode,
                  raterFileName,
                  raterDriveFileId
                )
              } catch (error) {
                console.error('Rater download failed:', error)
              } finally {
                setIsDownloading(false)
              }
            }}
            className={`inline-flex items-center gap-2 font-medium text-sm transition-colors ${
              isDownloading
                ? 'text-slate-400 cursor-not-allowed no-underline'
                : 'text-blue-600 hover:text-blue-700 hover:underline'
            }`}
            aria-disabled={isDownloading}
          >
            {isDownloading ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Preparing Rater Output...
              </>
            ) : (
              'Download Rater Output'
            )}
           </a>
      )}
        </div>
      </CardBody>
    </Card>
  )
}

/* ─── Page ───────────────────────────────────────────── */
export default function RunAuditPage() {
  const [auditResult, setAuditResult] = useState(null)
  const mutation = useRunAudit()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(auditSchema),
    defaultValues: { policy: ''},
  })

  const onSubmit = async ({ policy }) => {
    setAuditResult(null)
    try {
      const raw = await mutation.mutateAsync({ policy})
      setAuditResult(computeAuditResult(raw))
    } catch (_) {
    }
  }

  const handleReset = () => {
    reset()
    setAuditResult(null)
    mutation.reset()
  }

  return (
    <div className="animate-fade-in space-y-6">
      <Breadcrumb />

      <PageHeader
        title="Run Audit"
        description="Compare rater premium vs UI premium for a policy"
        actions={
          auditResult && (
            <button onClick={handleReset} className="btn-secondary">
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          )
        }
      />

      {/* Input Card */}
      <Card className="border-l-[6px] border-l-emerald-500">
        <CardHeader>
          <h2 className="section-title">Audit Parameters</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Enter the policy number to run a premium comparison
          </p>
        </CardHeader>

        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">

              {/* Policy Number */}
              <FormField
                label="Policy Number"
                required
                error={errors.policy?.message}
                // helper="Alphanumeric, e.g. TXRPBG-_______-00"
              >
                <Input
                  placeholder="TXRPBG-_______-00"
                  error={!!errors.policy}
                  className="font-mono"
                  {...register('policy')}
                />
              </FormField>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="btn-primary"
              >
                {mutation.isPending ? (
                  <>
                    <Spinner size="sm" />
                    Running comparison…
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Audit Policy
                  </>
                )}
              </button>
              {mutation.isPending ? (
                  <>
                    <i className='text-sm text-slate-500 mt-0.5'>The audit premium process is in progress and may take 2–3 minutes to complete.</i>
                  </>
                ) : ("")}

              {(auditResult || mutation.isError) && (
                <button type="button" onClick={handleReset} className="btn-secondary">
                  <RotateCcw className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Error State */}
      {mutation.isError && !mutation.isPending && (
        <Alert type="error" title="Audit failed">
          {mutation.error?.error || 'An unexpected error occurred. Please try again.'}
        </Alert>
      )}

      {/* Result */}
      {auditResult && !mutation.isPending && (
        <ResultCard result={auditResult} />
      )}

      {/* Empty State */}
      {!auditResult && !mutation.isPending && !mutation.isError && (
        <Card>
          <EmptyState
            icon={ClipboardList}
            title="No results yet"
            description="Enter a policy number and state above, then click Audit Policy to see results."
          />
        </Card>
      )}
    </div>
  )
}
